import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  ModalController,
} from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';
import { addIcons } from 'ionicons';
import {
  arrowBackOutline,
  arrowForwardOutline,
  checkmarkOutline,
  closeOutline,
} from 'ionicons/icons';

import { AppConfigService } from '../../../../../common/config/app.config.service';
import { MediaLimitsService } from '../../../service/media/media-limits.service';
import { ComponentAccess } from '../../../../../common/decorators/component-access.decorator';
import { AuthCta } from '../../../../../common/enums/auth-cta.enum';
import { CommonRoute } from '../../../../../common/enums/common-route.enum';
import { PlanChooserMessage } from '../../../../../common/enums/plan-chooser.enum';
import { PurchaseTimelineMessage } from '../../../../../common/enums/purchase-timeline.enum';
import {
  LandingPagePath,
  LegalLinkLabel,
} from '../../../../../common/enums/legal-url.enum';
import {
  billingIntervalRankAnnualFirst,
  billingIntervalSuffix,
} from '../../../../../common/enums/billing-interval-label.enum';
import {
  PAYWALL_COPY,
  ProFeature,
  ProMessage,
  mediaLimitsDisclosure,
  cloudRetentionNote,
} from '../../../../../common/enums/pro-feature-message.enum';
import { PlatformService } from '../../../../../common/services/platform/platform.service';
import { LogFactory } from '../../../../../common/services/logger/log-factory';
import { CountryAvailabilityService } from '../../../../../common/services/country-availability/country-availability.service';
import { MessageService } from '../../../service/message.service';
import {
  CheckoutDisclosure,
  PaymentPlanDto,
  PurchaseTimelineRow,
} from '../../models/payment.models';
import { CheckoutService } from '../../services/checkout.service';
import { ProGateService } from '../../services/pro-gate.service';
import { SubscriptionService } from '../../services/subscription.service';
import { AppRefreshCoordinator } from '../../../visibility/app-refresh-coordinator.service';
import { RefreshKey } from '../../../visibility/refresh-key.enum';
import {
  PlanChoice,
  PlanChooserComponent,
} from '../plan-chooser/plan-chooser.component';
import { PurchaseConsentComponent } from '../purchase-consent/purchase-consent.component';
import { PurchaseTimelineComponent } from '../purchase-timeline/purchase-timeline.component';

// Rider under a paid price the provider taxes itself (Polar as merchant of
// record, the app stores) — the exact amount is only known on their checkout.
const PLUS_APPLICABLE_TAXES = '+ applicable taxes';

// Tail of the per-plan trial badge — "7 days free, then" sits directly above the
// recurring price, so the price it introduces reads as what happens next.
const FREE_TRIAL_SUFFIX = 'free, then';

// Suffix on the paywall heading, which now leads with what the plan does rather
// than what the buyer is missing ("Sequal Pro — how it works").
const HOW_IT_WORKS_SUFFIX = '— how it works';

const MS_PER_DAY = 86_400_000;

// Native only: consent is asked in this modal, so without the terms nothing can
// be bought here.
const TERMS_LOAD_FAILED =
  'Current payment terms could not be loaded. Please try again.';

/**
 * The Pro paywall (ADR-0020, revised by the IAP central-plans epic / ADR-0066).
 * One component, three states, rendered per platform:
 *
 *   • guest (any platform)     → value props + the real price + two CTAs:
 *     "Create free account" and "Sign in". WEB shows the geo web price; NATIVE
 *     shows the device store's localized price (what they'll be charged after
 *     sign-in). Either CTA stores a pro-intent first, so after auth the flow
 *     resumes (WEB auto-checkout; NATIVE re-opens the paywall — see
 *     PaywallHostComponent.resumeProIntent).
 *   • signed-in, not Pro, WEB  → plan(s) + price + Subscribe (Polar/Stripe).
 *   • signed-in, not Pro, NATIVE → plan(s) + device store price + Buy (IAP), or
 *     "No plans available" when nothing is purchasable on this platform.
 *
 * Native prices (every state) come from the device account storefront — Apple
 * StoreKit / Google Play Billing key off the signed-in store ACCOUNT's country,
 * NOT the device region, so a US Apple ID shows USD even on an AU device. Every
 * price is rendered as "<ISO code> <localized symbol+amount>" (e.g. "AUD $12.34")
 * so AUD vs USD is never ambiguous while keeping the store's own symbol (£, ₹,
 * …) — see formatPrice().
 */
@Component({
  selector: 'app-paywall-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonContent,
    IonButton,
    IonIcon,
    IonSpinner,
    IonRadio,
    IonRadioGroup,
    PurchaseConsentComponent,
    PurchaseTimelineComponent,
  ],
  templateUrl: './paywall-modal.component.html',
  styleUrls: ['./paywall-modal.component.scss'],
})
@ComponentAccess('PaywallModalComponent')
export class PaywallModalComponent implements OnInit {
  @Input() feature: ProFeature = ProFeature.GoPremium;
  /** `guest` = signed-out; `needPlan` = signed-in, not Pro. */
  @Input() state: 'guest' | 'needPlan' = 'needPlan';

  private readonly modalCtrl = inject(ModalController);
  private readonly router = inject(Router);
  private readonly platform = inject(PlatformService);
  private readonly config = inject(AppConfigService);
  private readonly mediaLimits = inject(MediaLimitsService);
  private readonly checkout = inject(CheckoutService);
  private readonly proGate = inject(ProGateService);
  private readonly subscription = inject(SubscriptionService);
  private readonly refreshCoordinator = inject(AppRefreshCoordinator);
  private readonly toast = inject(MessageService);
  private readonly logger = inject(LogFactory).createLogger(PaywallModalComponent);
  private readonly countryAvailability = inject(CountryAvailabilityService);

  protected readonly isNative = this.platform.isNative();
  protected readonly plans = signal<PaymentPlanDto[]>([]);
  protected readonly selectedPlanId = signal<string | null>(null);
  protected readonly loadingPlans = signal<boolean>(false);
  protected readonly busy = signal<boolean>(false);
  /** No plan is purchasable on this platform (ADR-0037) — show the last-resort
   *  "No plans available" message instead of defaulting to any plan/gateway. */
  protected readonly plansUnavailable = signal<boolean>(false);
  /** Exposed for the template (shared-enum string rule). */
  protected readonly plansUnavailableMsg = ProMessage.PlansUnavailable;
  protected readonly priceUnavailableInlineMsg = ProMessage.PriceUnavailableInline;
  /** Guest CTAs — shared with the Discover landing so wording never drifts. */
  protected readonly createAccountLabel = AuthCta.CreateAccount;
  protected readonly signInLabel = AuthCta.SignIn;
  /** Opens the plan-group chooser (ADR-0123) — team plans are web-only. */
  protected readonly teamLinkLabel = PlanChooserMessage.TeamLink;
  /** Auto-renew terms shown above the legal links (App Store Guideline 3.1.2(c)). */
  protected readonly autoRenewDisclosure = ProMessage.AutoRenewDisclosure;
  /** Per-file media caps, disclosed before purchase because a subscription does
   *  NOT raise them. Null until the server limits land (and if they never do —
   *  the line is dropped rather than shown with guessed numbers). */
  protected readonly mediaLimitsNote = signal<string | null>(null);
  /** Functional Privacy Policy / Terms of Use (EULA) links — required in-app on
   *  the subscription screen (Guideline 3.1.2(c)). Rendered in every state.
   *  Resolved against THIS build's landing site, so each environment links its
   *  own copy; the pages must stay publicly reachable everywhere (see
   *  LandingPagePath). */
  protected readonly legalLinks: ReadonlyArray<{
    label: LegalLinkLabel;
    url: string;
  }> = [
    { label: LegalLinkLabel.PaymentTerms, path: LandingPagePath.Refunds },
    { label: LegalLinkLabel.Privacy, path: LandingPagePath.Privacy },
    { label: LegalLinkLabel.Terms, path: LandingPagePath.Terms },
    { label: LegalLinkLabel.Eula, path: LandingPagePath.Eula },
  ].map(({ label, path }) => ({ label, url: this.config.landingPageUrl(path) }));
  /** Historical app-managed trials may finish during the migration. Provider-
   *  backed trials are ordinary active subscriptions and never enter this path. */
  protected readonly onTrial = signal<boolean>(false);
  /** Timeline copy, for the template (shared-enum string rule). */
  protected readonly timelineCopy = PurchaseTimelineMessage;
  /**
   * Which step of the purchase the buyer is on.
   *
   * ONE SCREEN BY DEFAULT. What the plan gives you, what it costs and what
   * happens on which day all sit on the first screen — for a guest, a signed-in
   * buyer and an App Store reviewer alike (Guideline 3.1.2(c) wants the price,
   * the auto-renew disclosure and the legal links visible without clicking
   * through anything). The timeline used to be a step of its own; it now renders
   * inline, so there is nothing to tap past.
   *
   * Only native adds a second step: it never leaves the modal, so consent is
   * asked here. On web the checkout-confirmation route already IS that step and
   * owns the country picker.
   */
  protected readonly step = signal<'plan' | 'terms'>('plan');
  protected readonly disclosure = signal<CheckoutDisclosure | null>(null);
  protected readonly disclosureLoading = signal(false);
  protected readonly consentGiven = signal(false);
  /** Native + no connectivity: we can't reach our API to get the current store
   *  price, so we show a "connect to the internet" message and block buying. */
  protected readonly offline = signal<boolean>(false);
  /**
   * The country the CURRENT quote was priced for.
   *
   * Compared against `effectiveCountry`, which is what the API prices against —
   * `detectedCountry` alone would miss a buyer whose account country differs
   * from their IP, and that disagreement is exactly what made pricing and gating
   * contradict each other (ADR-0179).
   *
   * Advanced only once a refresh SUCCEEDS. Advancing it up front meant a second
   * country change arriving during an in-flight refresh was compared against a
   * country we had not actually priced for yet, and was dropped.
   *
   * Seeded from what we believe locally, then replaced in `ngOnInit` by the
   * `resolvedCountry` the API stamped on the plans it returned.
   */
  private quotedWebCountry =
    this.countryAvailability.availability()?.effectiveCountry ?? null;
  /** A country change that landed while a refresh was in flight. */
  private pendingWebCountry: string | null = null;
  /** First plan/price/terms load has settled. A signal, not a plain flag, because
   *  the template gates the full-cover overlay on it: only the FIRST load blanks
   *  the modal — a later plan switch must not, or every tap on a radio would
   *  wipe the screen the buyer is reading. */
  protected readonly paywallReady = signal(false);
  private webPlanRefresh: Promise<void> | null = null;

  /** Native price readiness for the SELECTED plan (the price is backend-resolved
   *  from the live store; web/guest is always 'na' and uses the web price):
   *   • 'offline'     → no connectivity (can't fetch price) — block checkout.
   *   • 'unavailable' → online but the store price couldn't be resolved — block
   *                     checkout.
   *   • 'ready'       → price present; buy enabled.
   *   • 'na'          → not applicable (web, guest, or no plans). */
  protected readonly nativePriceState = computed<
    'na' | 'offline' | 'unavailable' | 'ready'
  >(() => {
    // Native prices its plans in every state now (guest previewing + signed-in
    // buying); only web / no-plans short-circuits to 'na'.
    if (!this.isNative || this.plansUnavailable()) return 'na';
    if (this.offline()) return 'offline';
    const plan = this.plans().find((p) => p.id === this.selectedPlanId());
    if (plan && plan.priceAvailable === false) return 'unavailable';
    return 'ready';
  });

  /** Inline message for the offline/unavailable native price states ('' otherwise). */
  protected readonly pricingStateMsg = computed<string>(() => {
    switch (this.nativePriceState()) {
      case 'offline':
        return ProMessage.ConnectToSeePricing;
      case 'unavailable':
        return ProMessage.PriceUnavailable;
      default:
        return '';
    }
  });

  constructor() {
    addIcons({
      arrowBackOutline,
      arrowForwardOutline,
      checkmarkOutline,
      closeOutline,
    });

    // AppComponent force-refreshes the public country endpoint when a web tab
    // becomes visible or the window regains focus after a VPN switch. If that
    // request reports a different country while this paywall is open, discard
    // the previous web quote and ask the API for the newly routed plan prices.
    effect(() => {
      const current =
        this.countryAvailability.availability()?.effectiveCountry ?? null;
      if (current === this.quotedWebCountry) return;
      if (!this.paywallReady() || this.isNative) return;
      this.refreshWebPlansAfterCountryChange(current);
    });
  }

  protected get copy() {
    return PAYWALL_COPY[this.feature];
  }

  /** "Sequal Pro — how it works". The screen leads with what the plan does and
   *  when it charges, rather than with what the buyer is currently missing. */
  protected get heading(): string {
    return `${this.copy.title} ${HOW_IT_WORKS_SUFFIX}`;
  }

  protected get message(): string {
    if (this.state === 'guest') return ProMessage.GuestSignInThenPro;
    // Historical app-managed trial: they already have access, so "requires an
    // active plan" is wrong — invite them to move to a provider-backed plan.
    if (this.onTrial()) return ProMessage.TrialUpgradePitch;
    return ProMessage.RequiresActivePlan;
  }

  /** The currently-selected plan. */
  private selectedPlan(): PaymentPlanDto | undefined {
    return this.plans().find((p) => p.id === this.selectedPlanId());
  }

  /** Full-cover progress overlay: while plans + store prices load, AND while an
   *  action (subscribe / restore) is in flight. Covering the whole modal
   *  keeps the spinner off any single button — an inline button spinner wrongly
   *  reads as "only that button is loading" (see overlayMessage for the copy).
   *
   *  The terms fetch only counts before the paywall is ready. Once it is, a plan
   *  switch re-fetches them, and blanking the whole modal for that would wipe
   *  the screen out from under someone who just tapped a radio — the timeline
   *  block shows its own small spinner instead. */
  protected showLoadingOverlay(): boolean {
    return (
      this.loadingPlans() ||
      this.busy() ||
      (!this.paywallReady() && this.disclosureLoading())
    );
  }

  /** Copy under the overlay spinner: the price wait vs. an in-flight action.
   *  Price-loading wins when both are true (it's the first thing to settle). */
  protected get overlayMessage(): string {
    return this.loadingPlans() ? ProMessage.PriceLoading : ProMessage.Processing;
  }

  /** Native can buy whenever a plan is selected (ADR-0037): the backend returns
   *  only plans purchasable on this platform, and the resolved gateway in the
   *  initiate response drives the flow (IAP sheet vs hosted-checkout redirect). */
  protected nativeBuyable(): boolean {
    // Only buyable once the backend-resolved store price is ready (never offline
    // or price-unavailable — buying then would fail / mislead).
    return (
      this.isNative &&
      !!this.selectedPlanId() &&
      this.nativePriceState() === 'ready'
    );
  }

  /** Show price/plans: web always (guest + needPlan); native (guest + needPlan)
   *  once the device store price is ready. Hidden when none are available. */
  protected get showPrice(): boolean {
    if (this.plansUnavailable()) return false;
    if (!this.isNative) return true;
    // Native (guest preview + signed-in buy) shows the price block only when the
    // store price is ready; offline / unavailable render their own message
    // instead (see pricingStateMsg).
    return this.plans().length > 0 && this.nativePriceState() === 'ready';
  }

  protected get primaryLabel(): string {
    if (this.state === 'guest') return this.isNative ? 'Sign in' : 'Get Pro';
    // Nothing to buy (no plans, or native with none selected) → the button closes.
    if (this.plansUnavailable()) return 'Close';
    if (this.isNative && !this.nativeBuyable()) return 'Close';
    // Native still asks for consent on a step of its own, and that tap only
    // turns the page — it must not quote money it will not move, nor claim to
    // start a trial it does not start.
    if (this.advancesToTerms) return this.timelineCopy.Continue;
    // The tap from here buys. With a trial nothing is charged today, so the
    // button says what actually happens rather than quoting a price the buyer
    // will not pay yet.
    //
    // Only when we KNOW a trial applies: native reads the device offer (Apple
    // and Google decide eligibility per store account, so that answer is this
    // buyer's) and web reads the provider's own product. When we don't know, the
    // button makes no promise — "Start Pro", with the price and any taxed total
    // stated on the selected plan card directly above it.
    if (this.hasTrial()) return this.timelineCopy.StartTrial;
    // "Upgrade now" while converting a historical app-managed trial (ADR-0120).
    return this.onTrial() ? ProMessage.UpgradeNow : ProMessage.StartPro;
  }

  /** Hide "Not now" only when the primary button already IS the dismiss
   *  (no plans available, or native/signed-in with nothing to buy and no trial). */
  protected get showSecondary(): boolean {
    if (this.plansUnavailable()) return false;
    return !(
      this.isNative &&
      this.state === 'needPlan' &&
      !this.nativeBuyable()
    );
  }

  /** Restore-purchases affordance — required on native stores. */
  protected get showRestore(): boolean {
    return this.isNative && this.state === 'needPlan';
  }

  /** Whether a paid subscribe action can run: a plan is selected and (on native)
   *  its store price, disclosure, and fresh consent are ready. */
  protected get canSubscribe(): boolean {
    if (this.busy() || !this.selectedPlanId()) return false;
    if (!this.isNative) return true;
    const disclosure = this.disclosure();
    return (
      this.nativePriceState() === 'ready' &&
      !!disclosure &&
      (!disclosure.consentRequired || this.consentGiven())
    );
  }

  /** The primary button is disabled only when it's a "Subscribe" that has no
   *  selected plan; guest sign-in and any "Close" variant stay enabled. */
  /**
   * Whether the primary tap turns the page to consent instead of buying.
   *
   * One place, because the label, the action and the disabled rule all have to
   * agree about what the button does — and when they did not, the button that
   * OPENS the consent step was disabled until consent was given, which is only
   * possible on the step it opens. Nothing could be bought on native at all.
   */
  private get advancesToTerms(): boolean {
    return this.step() === 'plan' && this.isNative && !!this.disclosure();
  }

  protected get primaryDisabled(): boolean {
    if (this.busy()) return true;
    if (this.state === 'guest' || this.plansUnavailable()) return false;
    if (this.isNative && !this.nativeBuyable()) return false; // label is "Close"
    // Past that check a plan is selected and its store price is ready, which is
    // everything this tap needs: it only turns the page. Consent belongs to the
    // step it opens, so gating on it here is the deadlock described above.
    if (this.advancesToTerms) return false;
    return !this.canSubscribe;
  }

  protected toggleConsent(): void {
    if (this.busy()) return;
    this.consentGiven.update((given) => !given);
  }

  async ngOnInit(): Promise<void> {
    // The purchase sheet is what acts on purchase availability, so it pulls a
    // fresh answer when a resume or age made it stale (ADR-0264). Non-blocking:
    // the sheet already renders the cached answer and follows the signal.
    void this.refreshCoordinator.refreshIfStale(RefreshKey.PurchaseAvailability);

    // Fire-and-forget: the media caps are disclosure, not a gate on buying, so
    // they must never delay the paywall render. getLimits() never rejects — it
    // falls back to the cached copy and then to env — so this also fills in
    // offline, where the disclosure still has to be on screen.
    void this.mediaLimits
      .getLimits()
      .then((l) =>
        this.mediaLimitsNote.set(
          mediaLimitsDisclosure(l.maxVideoBytes, l.maxVideoDurationSec, l.maxAudioBytes),
        ),
      );

    // Native + offline: the price is backend-resolved, so without connectivity we
    // can't fetch it (the eligible-plans call would fail anyway). Short-circuit to
    // the "connect to the internet" state instead of "no plans available".
    if (this.isNative && this.state === 'needPlan' && !navigator.onLine) {
      this.offline.set(true);
      return;
    }

    // Show the loading overlay from the very start so it covers BOTH the
    // entitlement re-check (ADR-0068) and the plan/price fetch — no unguarded flash.
    this.loadingPlans.set(true);

    // Already-entitled guard (ADR-0068): a signed-in user may have become Pro on
    // another channel (e.g. bought on the web) since the local cache was set.
    // Re-verify against the server BEFORE showing any plans and dismiss with a
    // "you're already Pro" toast instead of offering a second (double) purchase.
    // Guests can't be Pro, so this only runs for needPlan; the same refresh also
    // settles the historical app-managed-trial migration path.
    if (this.state === 'needPlan') {
      let isPro = this.subscription.isPro;
      try {
        isPro = await this.subscription.refresh();
      } catch {
        /* keep the cached flag */
      }
      // Historical app-managed trial users may still move to a provider-backed
      // plan; all other entitled users are bounced with "already Pro".
      const onTrial = this.subscription.onTrial;
      this.onTrial.set(onTrial);
      if (isPro && !onTrial) {
        this.toast.add({ severity: 'success', detail: ProMessage.AlreadyPro });
        await this.dismiss();
        return;
      }
    }

    // Both web and native fetch plans now (ADR-0020 reversed). Keeping this in a
    // reusable loader also lets a still-open web paywall replace its quote when
    // the request-time country changes.
    await this.loadEligiblePlans();
    this.loadingPlans.set(false);
    this.paywallReady.set(true);
    if (!this.isNative) {
      // Adopt the country the API says it priced for, rather than a snapshot of
      // what we believed before the request. The stamp is the authoritative
      // answer, and taking it here also settles the case where availability
      // arrived (or changed) while the initial plan request was in flight — the
      // effect saw it but would not refresh a paywall that was not ready yet.
      this.quotedWebCountry = this.quotedCountry();
      const current =
        this.countryAvailability.availability()?.effectiveCountry ?? null;
      if (current !== this.quotedWebCountry) {
        this.refreshWebPlansAfterCountryChange(current);
      }
    }

    // Silent device-entitlement heal (ADR-0118). The already-Pro guard above
    // only asks OUR server; when its record is stale (a purchase whose verify
    // never landed, a wiped environment) the device still holds the store
    // entitlement — and offering Buy then is the same-group trap: the store
    // refuses or silently converts it. Re-verify device receipts server-side;
    // healed → same outcome as the guard. Runs AFTER the plan/price load so the
    // store plugin has initialized and replayed its entitlements; deliberately
    // not awaited — the paywall must not gate on it.
    if (this.state === 'needPlan' && this.isNative) {
      void this.healFromDeviceEntitlements();
    }
  }

  private async loadEligiblePlans(): Promise<void> {
    const selected = this.selectedPlanId();
    try {
      // Order ANNUAL first (year → month → week → day); the backend gives no
      // guaranteed order. Annual leads because it is the best value and most
      // subscription revenue is annual (ADR-0129).
      const plans = [...(await this.checkout.eligiblePlans())].sort(
        (a, b) =>
          billingIntervalRankAnnualFirst(a.billingInterval, b.billingInterval),
      );
      this.plans.set(plans);
      this.selectedPlanId.set(
        plans.some((plan) => plan.id === selected)
          ? selected
          : (plans[0]?.id ?? null),
      );
      // Native overlays StoreKit/Play Billing's localized account-storefront
      // quote. Web plans already carry the API's request-country quote.
      if (this.isNative && plans.length) {
        await this.applyDeviceStorePrices();
      }
      // Both surfaces now read the terms: they carry the trial length and the
      // dates the purchase timeline promises, which the first screen shows to
      // everyone — web included, where the modal used to leave that entirely to
      // the checkout-confirmation route.
      if (plans.length) await this.loadSelectedDisclosure();
    } catch {
      // Never leave the old country's quote purchasable after a failed refresh.
      this.plans.set([]);
      this.selectedPlanId.set(null);
    }

    // Last resort (ADR-0037): a signed-in user with nothing purchasable on this
    // platform sees "No plans available" instead of a defaulted plan/gateway.
    const unavailable =
      this.state === 'needPlan' && this.plans().length === 0;
    const becameUnavailable = unavailable && !this.plansUnavailable();
    this.plansUnavailable.set(unavailable);
    if (becameUnavailable) {
      this.toast.add({ severity: 'warn', detail: ProMessage.PlansUnavailable });
    }
  }

  /**
   * Re-quote for a country change while the paywall is open.
   *
   * A change arriving mid-refresh is QUEUED rather than dropped: someone
   * switching VPN twice in quick succession used to be left on the middle
   * country's prices, because the second change was discarded and the marker had
   * already been advanced past it.
   */
  private refreshWebPlansAfterCountryChange(country: string | null): void {
    if (this.webPlanRefresh) {
      this.pendingWebCountry = country;
      return;
    }
    this.loadingPlans.set(true);
    const refresh = this.loadEligiblePlans()
      .then(() => {
        // Only now is this the country we have actually priced for.
        this.quotedWebCountry = country;
      })
      .finally(() => {
        if (this.webPlanRefresh !== refresh) return;
        this.webPlanRefresh = null;
        this.loadingPlans.set(false);
        const queued = this.pendingWebCountry;
        this.pendingWebCountry = null;
        if (queued !== null && queued !== this.quotedWebCountry) {
          this.refreshWebPlansAfterCountryChange(queued);
        }
      });
    this.webPlanRefresh = refresh;
  }

  /** The country the plans on screen were priced for, as the API reported it.
   *  Falls back to what we believe locally when no plan carried a stamp. */
  private quotedCountry(): string | null {
    return (
      this.plans().find((plan) => plan.resolvedCountry)?.resolvedCountry ??
      this.quotedWebCountry
    );
  }

  /**
   * Re-check the buyer's country immediately before purchase and re-quote if it
   * moved, returning false when the prices on screen changed underneath them.
   *
   * The visibility/focus watcher only fires when the tab loses and regains
   * focus, so a move can go unnoticed while the paywall stays open; this is the
   * one check that runs at the moment it matters.
   */
  private async ensureQuoteMatchesCountry(): Promise<boolean> {
    let current: string | null;
    try {
      const availability = await firstValueFrom(
        this.countryAvailability.getAvailability(true),
      );
      current = availability.effectiveCountry;
    } catch {
      // Country unknown — the server still re-resolves it at checkout, so let
      // the purchase proceed rather than block on our own diagnostic.
      return true;
    }
    if (current === this.quotedCountry()) return true;

    this.refreshWebPlansAfterCountryChange(current);
    await this.webPlanRefresh;
    this.toast.add({
      severity: 'warn',
      detail: ProMessage.PricesUpdatedForLocation,
    });
    return false;
  }

  /** See ngOnInit — dismisses the paywall when the device's store entitlement
   *  turns out to be live and the server has re-adopted it. */
  private async healFromDeviceEntitlements(): Promise<void> {
    try {
      const healed = await this.checkout.restoreFromDevice();
      if (healed) {
        this.toast.add({ severity: 'success', detail: ProMessage.AlreadyPro });
        await this.dismiss();
      }
    } catch {
      // Heal is best-effort; the paywall stays usable without it.
    }
  }

  /**
   * On native, replace each IAP plan's display price with the device store's
   * localized price — the price the user is actually charged for their store
   * account. The backend's IP-territory price can't match the account storefront
   * (a Finland VPN is still charged the AU price), so the device is authoritative
   * here. A plan whose store price can't be read is marked priceAvailable=false
   * (the paywall then blocks checkout). Non-IAP native plans (physical → web
   * fallback) keep the backend price.
   */
  private async applyDeviceStorePrices(): Promise<void> {
    // Bulk-register every mapped SKU first: one store initialize + one catalog
    // round-trip for all plans, instead of a serial init/update per SKU.
    await this.checkout.registerStoreProducts(
      this.plans()
        .filter((p) => p.iapProductId)
        .map((p) => ({
          productId: p.iapProductId!,
          kind: p.iapProductKind,
          planType: p.planType,
          // Price the offer an admin nominated (ADR-0218), not the base one, so
          // the paywall shows what the store sheet will actually charge.
          offerId: p.iapOfferId,
          basePlanId: p.iapBasePlanId,
        })),
      true,
    );
    const priced = await Promise.all(
      this.plans().map(async (p) => {
        if (!p.iapProductId) return p;
        const store = await this.checkout.deviceStorePrice({
          productId: p.iapProductId,
          kind: p.iapProductKind,
          planType: p.planType,
          offerId: p.iapOfferId,
          basePlanId: p.iapBasePlanId,
        });
        return store
          ? {
              ...p,
              storeDisplayPrice: store.display,
              effectivePrice: store.amount ?? p.effectivePrice,
              currency: store.currency ?? p.currency,
              priceAvailable: true,
              nativeOffer: {
                trialOffered: store.trialOffered,
                trialDurationDays: store.trialDurationDays,
                offerId: store.offerId ?? null,
                basePlanId: store.basePlanId ?? null,
                price: store.amount ?? p.effectivePrice ?? undefined,
                currency: store.currency ?? p.currency,
              },
            }
          : {
              ...p,
              storeDisplayPrice: null,
              effectivePrice: null,
              priceAvailable: false,
              nativeOffer: null,
            };
      }),
    );
    this.plans.set(priced);
  }

  protected onSelect(id: string): void {
    this.selectedPlanId.set(id);
    this.consentGiven.set(false);
    // Changing plan re-opens the question the later steps answered: the timeline
    // dates and the terms both belong to the plan that was selected when they
    // were shown, so the flow restarts rather than carrying them over.
    this.step.set('plan');
    void this.loadSelectedDisclosure();
  }

  private async loadSelectedDisclosure(): Promise<void> {
    const plan = this.selectedPlan();
    // Native quotes the terms against the customer-specific store offer, so it
    // needs one; web has no such evidence and asks for the plan's own terms.
    if (!plan || (this.isNative && !plan.nativeOffer)) {
      this.disclosure.set(null);
      return;
    }
    const selectedId = plan.id;
    this.disclosureLoading.set(true);
    this.disclosure.set(null);
    this.consentGiven.set(false);
    try {
      const disclosure = await this.checkout.checkoutDisclosure(
        selectedId,
        undefined,
        plan.nativeOffer ?? undefined,
      );
      if (this.selectedPlanId() === selectedId) {
        this.disclosure.set(disclosure ?? null);
      }
    } catch (error) {
      this.logger.error('Checkout disclosure could not be loaded', error);
      // Only native is stuck: consent is asked in this modal, so without the
      // terms the buy button stays disabled and the buyer needs to know why. On
      // web the terms are what the timeline reads, not what the purchase needs —
      // the checkout-confirmation route asks for them again — so a red toast
      // over a screen that still works would be noise.
      if (this.isNative && this.selectedPlanId() === selectedId) {
        this.toast.add({ severity: 'error', detail: TERMS_LOAD_FAILED });
      }
    } finally {
      if (this.selectedPlanId() === selectedId) {
        this.disclosureLoading.set(false);
      }
    }
  }

  /** Primary CTA for the signed-in states (guest uses its own two buttons). */
  protected async primary(): Promise<void> {
    // Nothing purchasable on this platform — the button just closes (ADR-0037).
    if (this.plansUnavailable() || (this.isNative && !this.nativeBuyable())) {
      await this.dismiss();
      return;
    }
    // Native asks for consent on its own step before buying. This `return` is a
    // step boundary, not a refusal: the terms step is the one that subscribes.
    if (this.advancesToTerms) {
      this.step.set('terms');
      return;
    }
    // Subscribe to the selected plan; the resolved gateway drives the flow
    // (web/native → hosted-checkout redirect or native IAP sheet).
    await this.subscribe();
  }

  /** Back to the plan step; the header shows this instead of a close X. */
  protected back(): void {
    this.step.set('plan');
  }

  /**
   * Whether the selected plan comes with a free trial the buyer will actually
   * get — the server's disclosure, resolved for THIS buyer's rail and (on
   * native) their store account.
   *
   * Drives the timeline, the lead-in above the plans, the CTA and the footnote,
   * so all four either promise a trial together or none of them does. Guests are
   * included: the same disclosure is guest-readable, and a signed-out visitor
   * deciding whether to create an account is exactly who needs to see that the
   * first days cost nothing.
   */
  protected hasTrial(): boolean {
    const trial = this.disclosure()?.trial;
    return !!trial?.offered && !!trial.durationDays;
  }

  /** Trial days for a plan, from whichever source is authoritative here: the
   *  DEVICE offer on native (Apple/Google decide per store account) and the
   *  provider's synced product on web. 0 when no trial is offered. */
  protected trialDaysFor(plan: PaymentPlanDto | undefined): number {
    if (!plan) return 0;
    if (this.isNative) {
      return plan.nativeOffer?.trialOffered
        ? (plan.nativeOffer.trialDurationDays ?? 0)
        : 0;
    }
    return plan.trialDays ?? 0;
  }

  /**
   * "Cloud data kept 30 days if you cancel" for THIS plan, or '' when the plan
   * never deletes cloud data.
   *
   * Per plan, not per paywall: the grace is a plan setting, so a catalog whose
   * monthly and annual tiers keep data for different lengths says so on each
   * card rather than quoting whichever one happens to be selected. The number
   * is the server's resolved answer (the plan's own grace, else the configured
   * default) — the app never applies the fallback itself, or a config change
   * would leave the paywall promising a window the purge does not use.
   */
  protected retentionLabel(plan: PaymentPlanDto): string {
    const days = plan.cloudDataRetentionDays ?? 0;
    return days > 0 ? cloudRetentionNote(days) : '';
  }

  /**
   * "First 14 days are free, then as per your plan" — the lead-in above the plan
   * list, or '' when no trial applies.
   *
   * Says no price on purpose: the plans underneath it are still selectable, so a
   * figure here would go stale the moment the buyer taps a different one.
   * Prefers the disclosure (the rail's own answer) and falls back to the plan's
   * synced trial length while the disclosure for a freshly-picked plan loads, so
   * the line does not blink out mid-switch.
   */
  protected trialLeadIn(): string {
    const days =
      this.disclosure()?.trial.durationDays ||
      this.trialDaysFor(this.selectedPlan());
    if (!days || days <= 0) return '';
    const noun =
      days === 1
        ? PurchaseTimelineMessage.TrialLeadInSingular
        : PurchaseTimelineMessage.TrialLeadInPlural;
    return `${PurchaseTimelineMessage.TrialLeadInPrefix} ${days} ${noun}`;
  }

  /**
   * The purchase timeline, built from the server's own dates.
   *
   * Every row is a promise, so every row comes from the disclosure: the trial
   * length and first-charge date are the provider's, and the reminder row
   * appears ONLY when `trialReminderAt` is set — which the server leaves null
   * whenever that email will not actually be sent. A hard-coded "we'll remind
   * you" would be a promise no scheduler made.
   */
  protected readonly timelineRows = computed<PurchaseTimelineRow[]>(() => {
    const terms = this.disclosure();
    const days = terms?.trial.durationDays ?? 0;
    if (!terms || !terms.trial.offered || days <= 0) return [];

    const rows: PurchaseTimelineRow[] = [
      {
        when: PurchaseTimelineMessage.Today,
        title: PurchaseTimelineMessage.TodayTitle,
        detail: PurchaseTimelineMessage.TodayDetail,
        icon: 'lock-open-outline',
      },
    ];

    if (terms.trialReminderAt) {
      rows.push({
        when: this.reminderWhen(terms.trialReminderAt, terms, days),
        title: PurchaseTimelineMessage.ReminderTitle,
        detail: PurchaseTimelineMessage.ReminderDetail,
        icon: 'notifications-outline',
      });
    }

    const charge =
      terms.priceWithTax ?? terms.payablePrice ?? terms.price ?? 0;
    const plan = this.selectedPlan();
    // With the cadence, because this row is the whole answer to "what happens
    // when the free days run out" — an amount with no period leaves the buyer
    // to assume one.
    const cadence = plan ? this.frequencyLabel(plan) : '';
    const money = `${this.formatMoney(charge, terms.currency)}${cadence ? ` ${cadence}` : ''}`;
    rows.push({
      when: `${PurchaseTimelineMessage.DayPrefix} ${days}`,
      title: PurchaseTimelineMessage.ChargeTitle,
      detail: terms.firstChargeAt
        ? `${money} on ${this.formatDate(terms.firstChargeAt)}. ${PurchaseTimelineMessage.CancelBefore}`
        : `${money}. ${PurchaseTimelineMessage.CancelBefore}`,
      icon: 'star-outline',
    });
    return rows;
  });

  /**
   * "3 days before" — how much warning the reminder email gives, counted back
   * from the first charge.
   *
   * The lead time is derived, never assumed: the server sends at whatever
   * offsets are configured (and advertises the EARLIEST of them), so subtracting
   * its reminder date from its charge date is the only way this row can't drift
   * from what the scheduler will do. Falls back to "Day N" if either date is
   * unreadable, so the row still says something true.
   */
  private reminderWhen(
    reminderAt: string,
    terms: CheckoutDisclosure,
    trialDays: number,
  ): string {
    const remind = new Date(reminderAt).getTime();
    const charge = terms.firstChargeAt
      ? new Date(terms.firstChargeAt).getTime()
      : Date.now() + trialDays * MS_PER_DAY;
    const lead = Math.round((charge - remind) / MS_PER_DAY);
    if (Number.isNaN(lead) || lead <= 0) {
      return this.dayNumber(reminderAt, trialDays);
    }
    const noun =
      lead === 1
        ? PurchaseTimelineMessage.DayBeforeSuffix
        : PurchaseTimelineMessage.DaysBeforeSuffix;
    return `${lead} ${noun}`;
  }

  /** "Day N" for a date inside the trial, clamped to the trial's own length. */
  private dayNumber(iso: string, trialDays: number): string {
    const at = new Date(iso).getTime();
    if (Number.isNaN(at)) return PurchaseTimelineMessage.DayPrefix;
    const elapsed = Math.round((at - Date.now()) / MS_PER_DAY);
    const day = Math.min(Math.max(elapsed, 1), Math.max(trialDays - 1, 1));
    return `${PurchaseTimelineMessage.DayPrefix} ${day}`;
  }

  private formatDate(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  /** Restore previous purchases (App Store / Play requirement). */
  protected async restore(): Promise<void> {
    this.busy.set(true);
    let ok = false;
    try {
      ok = await this.checkout.restore();
    } finally {
      this.busy.set(false);
    }
    if (ok) {
      await this.dismiss();
      return;
    }
    // Restore succeeded as an operation but found no prior purchase to restore
    // for this account/store — tell the user exactly that (not "no plans"), so
    // the tap isn't a silent no-op and the message matches what happened.
    this.toast.add({ severity: 'info', detail: ProMessage.NoPurchaseToRestore });
  }

  private async subscribe(): Promise<void> {
    const planId = this.selectedPlanId();
    if (!planId) return;
    const selectedPlan = this.selectedPlan();
    const disclosure = this.disclosure();
    if (
      this.isNative &&
      (!selectedPlan?.nativeOffer ||
        !disclosure ||
        (disclosure.consentRequired && !this.consentGiven()))
    ) {
      return;
    }
    // Last check before money moves: has the buyer moved since this quote was
    // priced? The API stamps every plan with the country it priced for, so a
    // disagreement is detectable rather than silent — re-quote and make them
    // look at the new price rather than charge the old one (ADR-0179).
    if (!this.isNative && !(await this.ensureQuoteMatchesCountry())) return;

    this.busy.set(true);
    let ok = false;
    try {
      ok = await this.checkout.purchase(
        planId,
        this.isNative && disclosure && selectedPlan?.nativeOffer
          ? {
              purchaseConsent: {
                policyKey: disclosure.policyKey,
                version: disclosure.version,
                accepted: true,
              },
              nativeOffer: selectedPlan.nativeOffer,
            }
          : undefined,
      );
    } finally {
      // finally, not a trailing set: an unexpected throw used to leave busy
      // stuck true, which disables every control on the modal — the paywall
      // then looked frozen and only an app restart cleared it (ADR-0118).
      this.busy.set(false);
    }
    // On success the page navigates to Polar; only reached on failure/no-redirect.
    // A failure has already been explained by CheckoutService (which knows
    // whether it was a cancel, an already-Pro hit or a real error), so the modal
    // stays open without adding a second, vaguer message.
    if (ok) await this.dismiss();
  }

  /** Guest → create a free account. Remember the Pro intent AND the chosen plan
   *  first so the flow resumes with that plan after auth (web auto-checkout;
   *  native re-opens the paywall). */
  protected async signUp(): Promise<void> {
    this.proGate.setIntent(
      this.feature,
      this.selectedPlanId() ?? undefined,
      this.quotedCountry(),
    );
    await this.dismiss();
    void this.router.navigate([CommonRoute.SIGN_UP], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  /** Guest → sign in to an existing account (same intent-then-resume bridge). */
  protected async signIn(): Promise<void> {
    this.proGate.setIntent(
      this.feature,
      this.selectedPlanId() ?? undefined,
      this.quotedCountry(),
    );
    await this.dismiss();
    void this.router.navigate([CommonRoute.LOGIN], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  /** Billing-period suffix for the plan's price, e.g. "/ month" (Guideline
   *  3.1.2(c)); '' on a plan that does not recur. The period comes from the
   *  provider's own product, so it cannot disagree with what is charged. */
  protected frequencyLabel(plan: PaymentPlanDto): string {
    return billingIntervalSuffix(plan.billingInterval, plan.billingIntervalCount);
  }

  /** Open a legal page: in-app browser on native, new tab on web. */
  protected async openLegal(url: string): Promise<void> {
    if (this.isNative) {
      await Browser.open({ url });
    } else {
      window.open(url, '_blank', 'noopener');
    }
  }

  /** "N-day access" for any server-bounded grant, else '' (lifetime or
   *  subscription). The API, not this label, applies the expiry. */
  protected rentalLabel(plan: PaymentPlanDto): string {
    const days = plan.accessDays ?? plan.quantityValidDays ?? 0;
    return days > 0 ? `${days}-day access` : '';
  }

  /**
   * "7 days free, then" for a plan that will actually be trialled, else ''.
   *
   * On native this is read off the DEVICE offer, never a server number: Apple
   * and Google each decide trial eligibility per store account, so the sheet the
   * buyer is about to see is the only honest source. Web reads the provider's
   * own synced product. Per-plan, because trials differ between them — the
   * lead-in above the list speaks for the SELECTED plan only.
   */
  protected trialLabel(plan: PaymentPlanDto): string {
    const days = this.trialDaysFor(plan);
    if (days <= 0) return '';
    return `${days} ${days === 1 ? 'day' : 'days'} ${FREE_TRIAL_SUFFIX}`;
  }

  /** "50 downloads" for a consumable plan, else '' (nothing to count). */
  protected usageLabel(plan: PaymentPlanDto): string {
    const n = plan.quantity ?? 0;
    const unit = plan.quantityUnitLabel?.trim();
    if (n <= 0 || !unit) return '';
    return `${n} ${unit}${n === 1 ? '' : 's'}`;
  }

  protected formatPrice(plan: PaymentPlanDto): string {
    // Native IAP: prefer the device store's already-localized string — the exact
    // price the buyer sees on the store sheet for their account, authoritative
    // over the backend effectivePrice. Apple's displayPrice / Play's formattedPrice
    // keep the store's localized currency SYMBOL (£, ₹, $ …) but "$" alone is
    // ambiguous (AUD vs USD), so we prefix the ISO code: "AUD $12.34". Some
    // three-decimal currencies use the ISO code as their store display, so do
    // not duplicate it ("BHD BHD 1.235").
    if (plan.storeDisplayPrice) {
      const currency = plan.currency?.toUpperCase();
      return currency && !plan.storeDisplayPrice.toUpperCase().includes(currency)
        ? `${currency} ${plan.storeDisplayPrice}`
        : plan.storeDisplayPrice;
    }
    // The backend-resolved display price (web/guest). payablePrice is what the
    // checkout will charge — equal to effectivePrice unless a provider discount
    // is mapped (ADR-0218) — so quoting anything else would advertise a figure
    // the hosted page then contradicts. Null means "unavailable": the paywall
    // shows a message, not this, so render nothing rather than a misleading
    // fallback.
    const payable = plan.payablePrice ?? plan.effectivePrice;
    if (payable == null) return '';
    return this.formatMoney(payable, plan.currency ?? 'USD');
  }

  /**
   * The undiscounted price, for striking through beside the payable one.
   *
   * Web only, and only when a discount actually applies: on native the device
   * store's own string is the authoritative price and already reflects any store
   * offer, so there is no second figure of ours to show against it.
   */
  protected listPriceLabel(plan: PaymentPlanDto): string {
    if (plan.storeDisplayPrice) return '';
    if (!plan.discountAmount || plan.effectivePrice == null) return '';
    if (plan.payablePrice == null || plan.payablePrice >= plan.effectivePrice) {
      return '';
    }
    return this.formatMoney(plan.effectivePrice, plan.currency ?? 'USD');
  }

  /**
   * "20% off" — plus how long it lasts, when it ends.
   *
   * The rider is not decoration: a buyer told "20% off" who is charged full
   * price from the second period was misled by its absence, not by the number.
   * A permanent discount needs none, because the reduced price simply is the
   * price.
   */
  protected discountBadge(plan: PaymentPlanDto): string {
    if (plan.storeDisplayPrice || !plan.discountLabel) return '';
    return plan.discountDurationLabel
      ? `${plan.discountLabel} — ${plan.discountDurationLabel}`
      : plan.discountLabel;
  }

  private formatMoney(amount: number, currency: string): string {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  /**
   * Tax subline under the price. With an AU + Stripe quote the server resolved
   * the exact GST (ADR-0180): "+ AUD 0.50 GST (10%) — AUD 5.50 total", so the
   * hosted checkout's taxed total is never a surprise. Every other paid rail
   * settles tax at the provider (Polar as merchant of record, the app stores),
   * so the exact amount is unknowable here and a generic rider shows instead.
   * Empty for free plans and unavailable prices.
   */
  protected gstLabel(plan: PaymentPlanDto): string {
    if (
      plan.taxRatePercent != null &&
      plan.taxAmount != null &&
      plan.priceWithTax != null
    ) {
      const currency = plan.currency ?? 'USD';
      return `+ ${this.formatMoney(plan.taxAmount, currency)} GST (${Number(plan.taxRatePercent)}%) — ${this.formatMoney(plan.priceWithTax, currency)} total`;
    }
    const paid =
      !!plan.storeDisplayPrice ||
      (plan.effectivePrice != null && plan.effectivePrice > 0);
    return paid ? PLUS_APPLICABLE_TAXES : '';
  }

  // ─── Plan-group chooser (ADR-0123) ─────────────────────────────────────────

  /** Show the "Buying for a team?" link: web only (org plans are web-only) and
   *  only when the organisations feature is enabled. Native never sees it. */
  protected get showTeamLink(): boolean {
    return !this.isNative && this.config.isOrganizationsEnabled;
  }

  /**
   * Open the plan-group chooser over the paywall and act on the buyer's choice:
   *   • individual → pre-select that plan; they complete on the paywall with its
   *     own CTAs, so no purchase logic is duplicated.
   *   • org → close the paywall and hand off to the existing register-organization
   *     flow with the tier pre-selected (ADR-0100/0123) — purchase creates the org.
   */
  protected async openPlanChooser(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: PlanChooserComponent,
      cssClass: 'paywall-modal',
    });
    await modal.present();
    const { data } = await modal.onDidDismiss<PlanChoice>();
    if (!data) return;
    if (data.kind === 'individual') {
      this.onSelect(data.planId);
      return;
    }
    await this.dismiss();
    void this.router.navigate([CommonRoute.REGISTER_ORGANIZATION], {
      queryParams: { plan: data.tierId },
    });
  }

  protected dismiss(): Promise<boolean> {
    return this.modalCtrl.dismiss();
  }
}
